import React from 'react';
import { Dialog, DialogTitle, DialogContent, Box, ButtonBase, IconButton } from '@mui/material';
import { Close as CloseIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { EmptyState } from '../ui/EmptyState';
import { ShellGroup } from './useAppShellData';

interface NewChallengeDialogProps {
  open: boolean;
  onClose: () => void;
  groups: ShellGroup[];
}

// Challenges belong to a group, so the global "New challenge" button first asks which one.
// GroupDetail opens the contest form when it sees ?newChallenge=1.
export const NewChallengeDialog = ({ open, onClose, groups }: NewChallengeDialogProps) => {
  const { t } = useTranslation('layout');
  const navigate = useNavigate();

  const pick = (groupId: string) => {
    onClose();
    navigate(`/groups/${groupId}?newChallenge=1`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {t('newChallengeDialog.title')}
          <Box component="span" sx={{ fontSize: 13, fontWeight: 400, color: 'var(--muted)' }}>
            {t('newChallengeDialog.subtitle')}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label={t('common:actions.close')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 1.5, pb: 2 }}>
        {groups.length === 0 ? (
          <EmptyState title={t('newChallengeDialog.noGroupsTitle')} description={t('newChallengeDialog.noGroupsDescription')} />
        ) : (
          groups.map(g => (
            <ButtonBase
              key={g._id}
              onClick={() => pick(g._id)}
              sx={{ width: '100%', justifyContent: 'flex-start', gap: 1.5, p: '10px 12px', borderRadius: '8px', fontSize: 14, textAlign: 'left', '&:hover': { backgroundColor: 'var(--muted-bg)' } }}
            >
              <InitialsAvatar name={g.name} size={30} shape="rounded" />
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</Box>
              <ChevronRightIcon sx={{ fontSize: 18, color: 'var(--muted)' }} />
            </ButtonBase>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};
